import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { isOverdue } from '@platform/core/lib/format';
import type { AssignmentRole, AssignmentStatus } from '@platform/core/types';
import type { IAssignmentRowProps } from './IAssignmentRowProps';

const ROLES: AssignmentRole[] = ['Responsible', 'Contributor', 'Reviewer', 'Informed'];
const STATUSES: AssignmentStatus[] = ['To do', 'In progress', 'Done'];

/**
 * Рядок доручення (прототип v4, сітка 880px): виконано (прапорець) · хто («— Не призначено —» першим, як у прототипі; доручення без особи
 * сервер не зберігає, тож цей вибір нічого не змінює) · роль · що (поле на місці; виконане — закреслене) · термін (прострочений —
 * помаранчева рамка) · стан · × «Вилучити доручення». Виконані рядки — на --surface2. Без права зміни — лише читання.
 */
export function AssignmentRow({ assignment: a, members, canEdit, onChange, onRemove }: IAssignmentRowProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState(a.note ?? '');
  useEffect(() => setNote(a.note ?? ''), [a.note]);
  const done = a.status === 'Done';
  const late = !done && isOverdue(a.dueDate ?? null, false);
  const commitNote = () => { if (note !== (a.note ?? '')) onChange(a, { note: note || null }); };
  const others = members.some((m) => m.user.id === a.user.id) ? members : [{ user: a.user, role: 'Member' as const, joinedAt: '' }, ...members];
  return (
    <div className={cx('rtab-asg', done && 'is-done')}>
      <input type="checkbox" checked={done} disabled={!canEdit} aria-label={t('recordPage.assignments.toggle')} onChange={() => onChange(a, { status: done ? 'To do' : 'Done' })} />
      <select className="rtab-cell" value={a.user.id} disabled={!canEdit} aria-label={t('recordPage.assignments.who')}
        onChange={(e) => { if (e.target.value) onChange(a, { userId: e.target.value }); }}>
        <option value="">{t('recordPage.assignments.unassigned')}</option>
        {others.map((m) => <option key={m.user.id} value={m.user.id}>{m.user.name}</option>)}
      </select>
      <select className="rtab-cell" value={a.role} disabled={!canEdit} aria-label={t('recordPage.assignments.role')} onChange={(e) => onChange(a, { role: e.target.value as AssignmentRole })}>
        {ROLES.map((r) => <option key={r} value={r}>{t(`recordPage.assignments.roles.${r}`)}</option>)}
      </select>
      <input className="rtab-cell rtab-cell--note" value={note} readOnly={!canEdit} placeholder={t('recordPage.assignments.whatPlaceholder')} aria-label={t('recordPage.assignments.whatPlaceholder')}
        onChange={(e) => setNote(e.target.value)} onBlur={commitNote} onKeyDown={(e) => e.key === 'Enter' && commitNote()} />
      <input type="date" className={cx('rtab-cell', late && 'is-late')} value={(a.dueDate ?? '').slice(0, 10)} readOnly={!canEdit} aria-label={t('recordPage.assignments.due')}
        onChange={(e) => onChange(a, { dueDate: e.target.value || null })} />
      <select className="rtab-cell" value={a.status} disabled={!canEdit} aria-label={t('recordPage.assignments.status')} onChange={(e) => onChange(a, { status: e.target.value as AssignmentStatus })}>
        {STATUSES.map((s) => <option key={s} value={s}>{t(`recordPage.assignments.statuses.${s}`)}</option>)}
      </select>
      {canEdit ? <button type="button" className="rtab-asg__x" aria-label={t('recordPage.assignments.remove')} title={t('recordPage.assignments.remove')} onClick={() => onRemove(a)}>×</button> : <span />}
    </div>
  );
}
